/****************************************************
 * Module Name:  marketSellWolzaCriteriaWise.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { Box, styled, Tooltip, } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import { DataGridPro, GridToolbar, gridClasses } from "@mui/x-data-grid-pro";
import WolzaIndiaExportProxy from "../../utility/proxy/nivodautility/wolzaIndiaExportProxy";
import renderCellExpand from "./dataGridCellExpand";
import dayjs from "dayjs";

const StyledDataGridPro = styled(DataGridPro)({
    '.MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
        display: 'none',
    },
    '.MuiDataGrid-menuIcon': {
        width: '1px',
        fontSize: '1px'
    },
    [`& .${gridClasses.menuIconButton}`]: {
        marginRight: '-2px',
        fontSize: '1px',
        width: '1px',
        height: '1px',
    }
})

const MarketSellWolzaCriteriaWise = ({
    searchCriteria,
    fromDate,
    toDate,

}) => {
    const [listData, setListData] = useState([]);
    const [listLoader, setListLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({});

    // eslint-disable-next-line
    const [local_Permission, setLocal_Permission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MASTER_POTENTIALSELL
        )
    );

    useEffect(() => {
        handleSearch();
    }, []);

    const handleSearch = () => {

        let data = {
            from: (fromDate !== null && fromDate !== "") ?
                dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z" : null,
            to: (toDate !== null && toDate !== "") ?
                dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z" : null,
            clarity: searchCriteria.row.clarity,
            color: searchCriteria.row.color,
            cut: searchCriteria.row.cut,
            flo: searchCriteria.row.flo,
            pol: searchCriteria.row.pol,
            shape: searchCriteria.row.shape,
            sizerange: searchCriteria.row.sizerange,
            sym: searchCriteria.row.sym,
            stonetype: searchCriteria.row.stonetype && searchCriteria.row.stonetype === "NATURAL" ? "N" : "LG",
            lab: searchCriteria.row.lab,
        }

        setListLoader(true);
        WolzaIndiaExportProxy.GetDetailedMarketSell(data,
            (responseData) => {
                if (responseData) {
                    const uniqueFilterValues = {
                        shape: Utility.getDistinctOptions(responseData, "shape"),
                        cut: Utility.getDistinctOptions(responseData, "cut"),
                        clarity: Utility.getDistinctOptions(responseData, "clarity"),
                        pol: Utility.getDistinctOptions(responseData, "pol"),
                        sym: Utility.getDistinctOptions(responseData, "sym"),
                        flo: Utility.getDistinctOptions(responseData, "flo"),
                        color: Utility.getDistinctOptions(responseData, "color"),
                        stonetype: Utility.getDistinctOptions(responseData, "stonetype"),
                        lab: Utility.getDistinctOptions(responseData, "lab"),
                    };
                    setFilterValues(uniqueFilterValues);
                    setListData(responseData);
                }
                setListLoader(false);
            });
    };

    const columnsData = [
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 240,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "total_weight",
            headerName: "QTY",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COL",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "clarity",
            headerName: "CLR",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 190,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 190,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 190,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "total_price",
            headerName: "ESTIMATED FOB",
            headerAlign: "center",
            align: "center",
            width: 200,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
    ];

    const rows = listData;
    const columns = columnsData;

    return (

        <div>
            <div>
                <Box
                    sx={{
                        px: 1,
                        width: "100%",
                        position: "relative",
                        backgroundColor: '#fff',
                        color: '#000',
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "5px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: "75vh",
                    }}
                >
                    <StyledDataGridPro
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "6px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },
                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                        }}
                        density="compact"
                        disableRowSelectionOnClick={true}
                        slots={{ toolbar: GridToolbar }}
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={listLoader}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    locationid: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: {
                                    debounceMs: 500,
                                },
                            },
                        }}
                    />
                    <Box
                        style={{
                            position: "absolute",
                            top: 5,
                            right: "270px",
                        }}
                    >
                        {listLoader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => handleSearch()}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            </Tooltip>
                        )}

                    </Box>
                </Box>
            </div>
        </div>
    );
};
export default MarketSellWolzaCriteriaWise;
